import {
  Box,
  Button,
  ButtonGroup,
  Center,
  HStack,
  Spinner,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { PaloAltoMark } from '@gamma/ui/logos';
import { Alert, Modal } from '@gamma/ui/overlay';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { useCallback, useContext, useMemo } from 'react';
import { URLS, MISSING_DATA_PLACEHOLDER } from '@investigator/shared/constants';
import { i18n } from '@investigator/shared/localization';
import { AuthContext } from '@investigator/shared/providers';
import { EntityPanelRow } from '@investigator/features/detections/components';
import { NotConfiguredSection } from '../NotConfiguredSection';
import {
  EntityEdrStatus,
  useGetEDRConfiguration,
  useGetEDRConfigurationPublic,
} from '@investigator/features/entities/queries';
import { useEntitiesContext, useEntityEdrContext } from '../../../context';

const { integrations } = i18n.pages;
const { learnMore, additionalEdrIntegration, paloAlto, paloAltoTitle } =
  integrations;
const {
  unblockIPInPaloAlto,
  blockIPInPaloAlto,
  blockIP,
  unblockIP,
  unblockActionExplanation,
  blockActionExplanation,
} = paloAlto;

const { entityEDR } = i18n.pages.detections;
const { edrAction } = entityEDR;

const { PALO_ALTO_DOCS } = URLS;

export const PaloAltoModalSection = (): JSX.Element => {
  const { selectedEntity } = useEntitiesContext();
  const { paloAltoStatus, performEdrAction, performEdrActionLoading } =
    useEntityEdrContext();

  const { userRole, userLicense } = useContext(AuthContext);

  const { isOpen, onClose, onOpen } = useDisclosure();
  const { isLoading, entityName, tenantInfo } = selectedEntity || {};

  const { tenant_id } = tenantInfo || {};

  const entityId = `${tenant_id}-palo_alto_networks-endpoint_device-${entityName}`;
  const getEDRConfigQuery =
    userRole === 'admin'
      ? useGetEDRConfiguration
      : useGetEDRConfigurationPublic;

  const {
    data: configuration,
    loading: edrConfigLoading,
    error: edrConfigError,
  } = getEDRConfigQuery({
    skip: !tenant_id && !userLicense?.tenant,
    variables: {
      tenant: (tenant_id || userLicense?.tenant) ?? '',
      provider_name: 'palo_alto_networks',
    },
  });

  // Get status from context
  const { entity_edr_status } = paloAltoStatus.data || {};
  const getEntityEdrStatusLoading = paloAltoStatus.loading ?? true;
  const getEntityEdrStatusError = paloAltoStatus.error;

  // Handle perform EDR action using context
  const handlePerformEdrAction = useCallback(async () => {
    const action =
      entity_edr_status === EntityEdrStatus.IsolationRequested
        ? 'lift_isolation'
        : 'isolate';
    await performEdrAction({
      entityId,
      action,
      tenant: (tenant_id || userLicense?.tenant) ?? '',
      providerName: 'palo_alto_networks',
    });
    onClose();
  }, [
    entity_edr_status,
    performEdrAction,
    entityId,
    tenant_id,
    userLicense?.tenant,
    onClose,
  ]);

  const edrStatusAlert = useMemo(() => {
    if (entity_edr_status) {
      switch (entity_edr_status) {
        case EntityEdrStatus.Normal:
          return {
            colorScheme: 'green',
            statusText: edrAction.normal,
          };
        case EntityEdrStatus.Isolated:
          return {
            colorScheme: 'red',
            statusText: edrAction.blocked,
          };
        case EntityEdrStatus.IsolationRequested:
        case EntityEdrStatus.IsolationPending:
          return {
            colorScheme: 'orange',
            statusText: edrAction.pendingBlock,
          };
        case EntityEdrStatus.LiftIsolationPending:
        case EntityEdrStatus.LiftIsolationRequested:
          return {
            colorScheme: 'yellow',
            statusText: edrAction.pendingUnblock,
          };
      }
    }
    return undefined;
  }, [entity_edr_status]);

  return (
    <VStack w="100%" alignItems="start">
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={
          entity_edr_status === EntityEdrStatus.IsolationRequested
            ? unblockIPInPaloAlto
            : blockIPInPaloAlto
        }
        body={
          <VStack>
            <EntityPanelRow
              heading={edrAction.entityStatus}
              data-testid="palo-alto-entity-status"
              hasTestIdValue={false}
            >
              <Tag
                size="md"
                variant="solid"
                textAlign="left"
                colorScheme={edrStatusAlert?.colorScheme}
              >
                {edrStatusAlert?.statusText}
              </Tag>
            </EntityPanelRow>
            <EntityPanelRow heading={edrAction.entity}>
              {entityName}
            </EntityPanelRow>
            <Alert variant="subtle" status="warning">
              {entity_edr_status === EntityEdrStatus.IsolationRequested
                ? i18n.formatString(unblockActionExplanation, entityName || '')
                : i18n.formatString(blockActionExplanation, entityName || '')}
            </Alert>
          </VStack>
        }
        footer={
          <ButtonGroup>
            <Button variant="solid" colorScheme="gray" onClick={onClose}>
              {edrAction.cancel}
            </Button>
            <Button
              variant="solid"
              colorScheme="red"
              isLoading={performEdrActionLoading}
              data-testid="suppress-entity-submit"
              onClick={handlePerformEdrAction}
            >
              {entity_edr_status === EntityEdrStatus.IsolationRequested
                ? unblockIP
                : blockIP}
            </Button>
          </ButtonGroup>
        }
      ></Modal>
      <HStack data-testid="palo-alto-header">
        <PaloAltoMark />
        <Text textStyle="h5">{paloAltoTitle}</Text>
      </HStack>
      {edrConfigLoading ||
      isLoading ||
      edrConfigError ||
      getEntityEdrStatusLoading ||
      getEntityEdrStatusError ? (
        <Center w="100%">
          <GraphQLReqStatus
            error={getEntityEdrStatusError || edrConfigError}
            loading={
              !!isLoading || edrConfigLoading || getEntityEdrStatusLoading
            }
          />
        </Center>
      ) : configuration?.getEDRConfiguration === null ||
        configuration?.getEDRConfiguration.enabled === false ? (
        <NotConfiguredSection
          message={additionalEdrIntegration}
          buttonText={learnMore}
          buttonLink={PALO_ALTO_DOCS}
          buttonTargetBlank={true}
        />
      ) : (
        <HStack w="100%" spacing={8} alignItems="start">
          <EntityPanelRow
            heading={edrAction.entityStatus}
            data-testid="palo-alto-entity-status"
            hasTestIdValue={false}
          >
            <HStack>
              {edrStatusAlert ? (
                <HStack spacing={0}>
                  <Tag
                    size="md"
                    variant="solid"
                    textAlign="center"
                    colorScheme={edrStatusAlert?.colorScheme}
                    data-testid="palo-alto-entity-status-status-value"
                  >
                    {edrStatusAlert?.statusText}
                  </Tag>
                  <Tooltip
                    w="120px"
                    placement="top"
                    label={
                      userRole !== 'admin'
                        ? edrAction.requestPermissions
                        : undefined
                    }
                  >
                    <Button
                      data-testid={`palo-alto-entity-status-action-value`}
                      marginLeft={1}
                      size="xs"
                      variant="solid"
                      colorScheme="gray"
                      onClick={onOpen}
                      isDisabled={
                        userRole !== 'admin' || getEntityEdrStatusLoading
                      }
                    >
                      {entity_edr_status === EntityEdrStatus.IsolationRequested
                        ? unblockIP
                        : blockIP}
                    </Button>
                  </Tooltip>
                </HStack>
              ) : getEntityEdrStatusLoading ? (
                <Box w="20px" h="20px">
                  <Spinner size="xs" color="white" />
                </Box>
              ) : (
                <Text data-testid="palo-alto-entity-status-value">
                  {MISSING_DATA_PLACEHOLDER}
                </Text>
              )}
            </HStack>
          </EntityPanelRow>
        </HStack>
      )}
    </VStack>
  );
};
